import * as React from "react";
import { useTheme } from "styled-components";
import type { TypeTheme } from "@sproutsocial/seeds-react-theme";

/**
 * Returns `true` when the viewport matches a mobile-width media query.
 *
 * @param breakpoint Optional override. Accepts a CSS string (`"768px"`,
 *   `"40em"`) or a number (interpreted as px). Defaults to the styled-components
 *   theme's `breakpoints.md` (falling back to `"768px"` if the theme is
 *   unavailable).
 */
export function useIsMobile(breakpoint?: number | string): boolean {
  const styledTheme = useTheme() as TypeTheme | undefined;
  const themeMd = styledTheme?.breakpoints?.md ?? "768px";
  const resolved =
    typeof breakpoint === "number" ? `${breakpoint}px` : breakpoint ?? themeMd;

  const [isMobile, setIsMobile] = React.useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return false;
    }
    return window.matchMedia(`(max-width: ${resolved})`).matches;
  });

  React.useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const mq = window.matchMedia(`(max-width: ${resolved})`);
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [resolved]);

  return isMobile;
}
